import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import swal from "sweetalert";
import HttpCommon from "../../Utils/http-common";
import {
  MUST_SET_PASSWORD_KEY,
  TOKEN_KEY,
  USERID_KEY,
} from "../../Frontend/Constant";
import {
  APP_OPEN_WAIT_MS,
  buildAppWhitelistDeepLink,
  isIosUserAgent,
  normalizeWhitelistToken,
  shouldAttemptAppDeepLink,
  shouldFallbackToWeb,
} from "./whitelistLoginBridge";

/** Survives React Strict Mode remounts within the same page load. */
let consumeInFlightKey = null;

function goToDashboard(navigate, userId, sessionToken) {
  navigate(`/dashboard/?id=${userId}&token=${sessionToken}&subuser=false`, {
    replace: true,
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function tryOpenAppDeepLink(deepLink, userAgent = "") {
  // iOS Safari often ignores custom-scheme iframes; use location.
  if (isIosUserAgent(userAgent)) {
    window.location.href = deepLink;
    return;
  }
  // Android: hidden iframe keeps the tab available for web fallback.
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = deepLink;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    try {
      iframe.remove();
    } catch (_) {
      /* ignore */
    }
  }, APP_OPEN_WAIT_MS + 200);
}

export default function WhitelistLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const finishedRef = useRef(false);
  const [phase, setPhase] = useState("loading"); // loading | opening_app | app_hint | signing_in
  const [token, setToken] = useState(null);

  const consumeOnWeb = async (sessionToken) => {
    if (consumeInFlightKey === sessionToken) return;
    consumeInFlightKey = sessionToken;
    setPhase("signing_in");

    try {
      const res = await HttpCommon.post("/user/whitelist-consume-token", {
        token: sessionToken,
      });
      if (res?.data?.success === 1) {
        const user_id = res.data.user_id;
        const authToken = res.data.token;
        if (res.data.must_set_password) {
          localStorage.setItem(MUST_SET_PASSWORD_KEY, "true");
        }
        localStorage.setItem(TOKEN_KEY, authToken);
        localStorage.setItem(USERID_KEY, user_id);
        finishedRef.current = true;
        goToDashboard(navigate, user_id, authToken);
        return;
      }
      if (finishedRef.current) return;
      swal(
        "Unable to sign in",
        res?.data?.message || "Link expired or invalid. Please request a new link.",
        "error",
      ).then(() => navigate("/account"));
    } catch (err) {
      if (finishedRef.current) return;

      const status = err?.response?.status;
      const msg = err?.response?.data?.message || "";
      const existingToken = localStorage.getItem(TOKEN_KEY);
      const existingUserId = localStorage.getItem(USERID_KEY);

      if (existingToken && existingUserId) {
        finishedRef.current = true;
        goToDashboard(navigate, existingUserId, existingToken);
        return;
      }

      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
        return;
      }

      if (status === 403 || msg === "Token is not Valid") {
        // Fall through to message below when no session exists.
      }

      swal(
        "Unable to sign in",
        msg || "Unable to complete sign-in. Please request a new link and try again.",
        "error",
      ).then(() => navigate("/account"));
    } finally {
      if (consumeInFlightKey === sessionToken) {
        consumeInFlightKey = null;
      }
    }
  };

  useEffect(() => {
    const normalized = normalizeWhitelistToken(searchParams.get("token"));
    setToken(normalized);

    if (!normalized) {
      swal("Invalid link", "Please request a new sign-in link from login.", "error").then(
        () => navigate("/account"),
      );
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    let pageWasHidden = typeof document !== "undefined" && document.hidden;

    const onVisibility = () => {
      if (document.hidden) pageWasHidden = true;
    };
    const onPageHide = () => {
      pageWasHidden = true;
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    (async () => {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const attemptApp = shouldAttemptAppDeepLink(ua);
      const deepLink = buildAppWhitelistDeepLink(normalized);

      if (attemptApp && deepLink) {
        setPhase("opening_app");
        tryOpenAppDeepLink(deepLink, ua);
        await sleep(APP_OPEN_WAIT_MS);
        if (cancelled) return;

        if (!shouldFallbackToWeb({ pageWasHidden })) {
          // App likely opened — do not consume the token on web.
          setPhase("app_hint");
          return;
        }
      }

      if (cancelled) return;
      await consumeOnWeb(normalized);
    })();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        px: 2,
        textAlign: "center",
      }}
    >
      {(phase === "loading" || phase === "opening_app" || phase === "signing_in") && (
        <>
          <CircularProgress style={{ color: "black" }} />
          <Typography variant="body2" color="text.secondary">
            {phase === "opening_app"
              ? "Trying to open the PawprintID app…"
              : phase === "signing_in"
                ? "Signing you in…"
                : "Loading…"}
          </Typography>
        </>
      )}

      {phase === "app_hint" && (
        <>
          <Typography variant="h6">Continue in the app</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
            If the PawprintID app opened, finish signing in there. Otherwise continue in
            your browser.
          </Typography>
          <Button
            variant="contained"
            onClick={() => token && consumeOnWeb(token)}
            sx={{ mt: 1 }}
          >
            Continue in browser
          </Button>
        </>
      )}
    </Box>
  );
}
